import { required, minValue, useCanAccess, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { ArrayInput } from "@/components/admin/array-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";

import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput";
import { contactOptionText } from "../misc/ContactOption";
import {
  formatUsd,
  itemsSubtotal,
  tradeTypeChoices,
  type ShipmentItem,
} from "./types";

const today = () => new Date().toISOString().split("T")[0];

export const ShipmentInputs = () => {
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const companyId = useWatch({ name: "company_id" });
  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="grid gap-4 md:grid-cols-3">
        <DateInput
          source="invoice_date"
          label="egi.fields.invoice_date"
          defaultValue={today()}
          validate={required()}
          helperText={false}
        />
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteCompanyInput
            label="egi.fields.customer"
            validate={required()}
            modal
          />
        </ReferenceInput>
        <ReferenceInput
          source="contact_id"
          reference="contacts_summary"
          filter={companyId ? { company_id: companyId } : {}}
        >
          <AutocompleteInput
            label="egi.fields.contact"
            optionText={contactOptionText}
            helperText={false}
          />
        </ReferenceInput>
      </div>

      <ArrayInput
        source="items"
        label="egi.fields.products"
        validate={required()}
      >
        <SimpleFormIterator inline disableReordering>
          <TextInput
            source="description"
            label="egi.fields.product"
            validate={required()}
            helperText={false}
            className="min-w-56 flex-[3]"
          />
          <TextInput
            source="details"
            label="egi.fields.details"
            helperText={false}
            className="min-w-40 flex-[2]"
          />
          <NumberInput
            source="quantity"
            label="egi.fields.quantity"
            validate={[required(), minValue(0)]}
            helperText={false}
            className="w-28"
          />
          <NumberInput
            source="unit_price"
            label="egi.fields.unit_price"
            validate={[required(), minValue(0)]}
            helperText={false}
            className="w-28"
          />
        </SimpleFormIterator>
      </ArrayInput>

      <div className="grid gap-4 md:grid-cols-3 items-start">
        <NumberInput source="tax" label="egi.fields.tax" helperText={false} />
        <SelectInput
          source="trade_type"
          label="egi.trade_type.label"
          choices={tradeTypeChoices}
          defaultValue="external"
          validate={required()}
          helperText={false}
        />
        {isAdmin ? (
          <ReferenceInput source="sales_id" reference="sales">
            <SelectInput
              label="egi.fields.salesperson"
              optionText={(s) => `${s.first_name} ${s.last_name}`}
              validate={required()}
              helperText={false}
            />
          </ReferenceInput>
        ) : null}
        <TotalPreview />
      </div>
      <TextInput
        source="notes"
        label="egi.fields.notes"
        multiline
        rows={2}
        helperText={false}
      />
    </div>
  );
};

const TotalPreview = () => {
  const translate = useTranslate();
  const items = useWatch({ name: "items" }) as Partial<ShipmentItem>[];
  const tax = Number(useWatch({ name: "tax" }) || 0);
  return (
    <div className="md:col-span-3 text-right">
      <div className="text-sm text-muted-foreground">
        {translate("egi.fields.total")}
      </div>
      <div className="text-2xl font-semibold tabular-nums">
        {formatUsd(itemsSubtotal(items) + tax)}
      </div>
    </div>
  );
};
